'use client';

import React, { useState, useRef, useEffect } from 'react';
import CustomRichText from '../../blog/components/CustomRichText';
import { ImageField, RichTextField } from '@prismicio/types';
import Image from 'next/image';

interface Benefit {
  title: string;
  description: RichTextField;
  list: RichTextField;
  image?: ImageField;
}

interface BenefitTabsDisplayProps {
  benefits?: Benefit[];
}

const DUMMY_BENEFITS: Benefit[] = [
  // ... (copy the DUMMY_BENEFITS array from BenefitTabs.tsx)
];

const IMAGE_COLORS = [
  'bg-blue-200',
  'bg-green-200',
  'bg-yellow-200',
  'bg-purple-200',
  'bg-pink-200',
  'bg-indigo-200',
];

// Presentational card shared by the mobile (single, natural-height) and desktop
// (grid-stacked cross-fade) layouts. `flex-shrink-0` on the media box is what
// guarantees the image keeps its aspect ratio instead of being crushed by the
// flex column — the source of the earlier squished/hard-cropped renders.
const BenefitCard: React.FC<{ tab: Benefit; index: number; priority?: boolean }> = ({
  tab,
  index,
  priority,
}) => (
  <div className="flex h-full w-full flex-col gap-6 rounded-xl bg-white p-8 pb-10 shadow">
    {tab.image?.url ? (
      <div className="relative aspect-[18/9] w-full flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
        <Image
          src={tab.image.url}
          alt={tab.image.alt || tab.title || 'Benefit illustration'}
          fill
          className="object-cover opacity-80"
          sizes="(max-width: 768px) 100vw, 800px"
          priority={priority}
        />
      </div>
    ) : (
      <div
        className={`aspect-[18/9] w-full flex-shrink-0 rounded-lg ${IMAGE_COLORS[index % IMAGE_COLORS.length]}`}
      />
    )}
    <div className="text-base text-black sm:text-xl">
      <CustomRichText field={tab.description} />
    </div>
    <ul className="text-base text-gray-500">
      <CustomRichText
        field={tab.list}
        components={{
          listItem: ({ children }) => (
            <li className="mb-2 flex items-start gap-2">
              <svg
                className="mr-1 mt-1 flex-shrink-0"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="10" cy="10" r="10" fill="#eeeeee" />
                <path
                  d="M6 10.5l2.5 2.5L14 7.5"
                  stroke="#BCA154"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{children}</span>
            </li>
          ),
        }}
      />
    </ul>
  </div>
);

const BenefitTabsDisplay: React.FC<BenefitTabsDisplayProps> = ({ benefits }) => {
  const TABS = benefits && benefits.length > 0 ? benefits : DUMMY_BENEFITS;
  const [active, setActive] = useState(0);
  const tabListRef = useRef<HTMLUListElement | null>(null);
  const pillsScrollRef = useRef<HTMLDivElement | null>(null);
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorPos, setIndicatorPos] = useState(0);
  const [indicatorHeight, setIndicatorHeight] = useState(56);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-cycle tabs on desktop when not hovered
  useEffect(() => {
    const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;
    let interval: NodeJS.Timeout | null = null;
    if (!isHovered && isDesktop()) {
      interval = setInterval(() => {
        setActive((prev) => (prev + 1) % TABS.length);
      }, 4000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isHovered, TABS.length]);

  // Move the desktop rail indicator to the active tab.
  useEffect(() => {
    if (tabListRef.current) {
      const tabEls = tabListRef.current.querySelectorAll('li');
      if (tabEls[active]) {
        const el = tabEls[active] as HTMLElement;
        setIndicatorPos(el.offsetTop);
        setIndicatorHeight(el.offsetHeight);
      }
    }
  }, [active]);

  // Keep the active mobile pill scrolled into view within its own scroller
  // (container-scoped — never scrolls the page, harmless when hidden at md+).
  useEffect(() => {
    const container = pillsScrollRef.current;
    const pill = pillRefs.current[active];
    if (!container || !pill) return;
    const target = pill.offsetLeft - container.clientWidth / 2 + pill.clientWidth / 2;
    container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [active]);

  return (
    <div
      className="tab-container container"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-8 md:flex-row">
        {/* Tabs (vertical rail on desktop, scrollable pills on mobile) */}
        <div className="flex w-full flex-col md:w-1/3">
          {/* Mobile pills — horizontally scrollable, bleed to the screen edge */}
          <div className="md:hidden">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
              Select a Benefit
            </p>
            <div
              ref={pillsScrollRef}
              className="no-scrollbar -mx-4 flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-px-4 px-4 pb-1 [touch-action:pan-x]"
            >
              {TABS.map((tab, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={tab.title}
                    ref={(el) => {
                      pillRefs.current[i] = el;
                    }}
                    type="button"
                    onClick={() => setActive(i)}
                    aria-pressed={isActive}
                    className={`group flex shrink-0 snap-start items-center gap-2 rounded-full border py-2.5 pl-3 pr-4 text-left text-sm font-semibold transition-colors duration-200 ${
                      isActive
                        ? 'border-blue-500 bg-blue-500 text-white shadow-sm'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-500/40 hover:text-blue-500'
                    }`}
                  >
                    <span
                      className={`text-xs font-bold tabular-nums ${
                        isActive ? 'text-white/70' : 'text-gray-400 group-hover:text-blue-500/60'
                      }`}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="whitespace-nowrap">{tab.title}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Desktop vertical tabs */}
          <div className="relative hidden md:block">
            {/* Animated indicator */}
            <div
              className="absolute left-0 top-0 w-1 rounded bg-yellow-300 transition-transform duration-300"
              style={{
                height: indicatorHeight,
                transform: `translateY(${indicatorPos}px)`,
                transition: 'transform 0.4s cubic-bezier(.34,1.56,.64,1)',
              }}
            />
            <ul className="flex flex-col border-l border-gray-300" ref={tabListRef}>
              {TABS.map((tab, i) => (
                <li
                  key={tab.title}
                  className={`relative cursor-pointer px-6 py-4 text-base font-semibold transition duration-200 ${i === active ? 'text-yellow-500 opacity-100' : 'text-gray-500 opacity-50 hover:text-yellow-500 hover:opacity-100'}`}
                  onClick={() => setActive(i)}
                  style={{ minHeight: 56 }}
                >
                  {tab.title}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Content — mobile: single active card at natural height */}
        <div key={active} className="w-full animate-in fade-in-0 duration-300 md:hidden">
          <BenefitCard tab={TABS[active]} index={active} priority />
        </div>

        {/* Content — desktop: grid-stacked cards cross-fade (auto-sizes to the
            tallest card, so no JS height measurement and no squished images) */}
        <div className="hidden w-full md:grid md:w-2/3">
          {TABS.map((tab, i) => (
            <div
              key={tab.title}
              className={`transition-opacity duration-300 [grid-area:1/1] ${
                i === active ? 'z-10 opacity-100' : 'pointer-events-none z-0 opacity-0'
              }`}
            >
              <BenefitCard tab={tab} index={i} priority={i === 0} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BenefitTabsDisplay;
